import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Button } from 'primereact/button';
import { useOrders } from '@/entities/Order/model/store';
import {
    COMPLETE_ORDER_DEFAULTS,
    completeOrderSchema,
    type CompleteOrderFormValues,
} from '@/entities/Order/model/completeOrderSchema';
import { ImageFilePicker } from '@/shared/ui/ImageFilePicker/ImageFilePicker';
import { Modal } from '@/shared/ui/Modal/Modal';
import { RequestError, RequestLoading } from '@/shared/ui/RequestState';
import { createRHFFields } from '@/shared/ui/RHFFields';
import { useCompleteOrderSubmit } from './useCompleteOrderSubmit';
import classes from './CompleteOrderModal.module.scss';

interface CompleteOrderModalProps {
    visible: boolean;
    orderId: string;
    onHide: () => void;
}

const { TextareaField } = createRHFFields<CompleteOrderFormValues>();

/** Показывает форму отчёта для завершения заказа */
export const CompleteOrderModal = ({ visible, orderId, onHide }: CompleteOrderModalProps) => {
    const [previewOpen, setPreviewOpen] = useState(false);
    const getOrderById = useOrders((state) => state.getOrderById);

    const form = useForm<CompleteOrderFormValues>({
        resolver: zodResolver(completeOrderSchema),
        defaultValues: COMPLETE_ORDER_DEFAULTS,
    });

    const { onSubmit, progress, resetUploads } = useCompleteOrderSubmit(orderId, form, () => {
        onHide();
        void getOrderById(orderId);
    });

    const { reset, formState: { isSubmitting, errors } } = form;

    useEffect(() => {
        reset(COMPLETE_ORDER_DEFAULTS);
        resetUploads();
        setPreviewOpen(false);
    }, [orderId, visible, reset, resetUploads]);

    /** Закрывает форму отчёта */
    const handleHide = () => {
        if (!isSubmitting && !previewOpen) onHide();
    };

    const photoError = errors.photos?.message ?? errors.photos?.find?.((error) => error?.message)?.message;

    return <Modal
        visible={visible}
        onHide={handleHide}
        title="Завершить заказ"
        size="lg"
        closable={!isSubmitting && !previewOpen}
        className={classes.modal}
        footer={<div className={classes.footer}>
            <Button
                type="button"
                label="Отмена"
                text
                disabled={isSubmitting}
                onClick={handleHide}
            />
            <Button
                type="submit"
                form="complete-order-form"
                label="Завершить заказ"
                icon="pi pi-check-circle"
                loading={isSubmitting}
                disabled={isSubmitting || !orderId}
            />
        </div>}
    >
        <FormProvider {...form}>
            <form
                id="complete-order-form"
                className={classes.form}
                noValidate
                onSubmit={form.handleSubmit(onSubmit)}
            >
                <TextareaField
                    name="description"
                    label="Текст отчёта"
                    placeholder="Расскажите, как прошёл заказ"
                    rows={4}
                    autoResize={false}
                    isRequired
                    disabled={isSubmitting}
                />
                <Controller
                    name="photos"
                    control={form.control}
                    render={({ field }) => (
                        <ImageFilePicker
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            onPreviewChange={setPreviewOpen}
                            disabled={isSubmitting}
                            error={photoError}
                        />
                    )}
                />
                {isSubmitting &&
                    <RequestLoading message={progress} />
                }
                {errors.root?.server?.message &&
                    <RequestError
                        message={errors.root.server.message}
                        compact
                    />
                }
            </form>
        </FormProvider>
    </Modal>;
};
